import moment from 'moment';
import { gcApi } from 'config/axios';
import React from 'react';
import { MaxHydrationValues } from 'utils/HydrationUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import NavigationUtil from 'utils/NavigationUtil';
import DateFormatter from 'utils/date/DateFormatter';
import {
    CYCLE_TYPE_PREGNANT,
    CYCLE_TYPE_INACTIVE,
    CYCLE_TYPE_REGULAR,
    CYCLE_TYPE_IRREGULAR
} from 'constants/MenstrualCycleTrackingConstants';
import Translate from 'components/Translate';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from 'ui-connect';
import i18next from 'i18next';

export const MAX_NUMBER_OF_WEEKS = 42;

export const childTypes = [
    { value: 'SINGLE', label_translation: 'label_single_child' },
    { value: 'MULTIPLE', label_translation: 'label_multiple_children' }
];

export const glucoseUnits = [
    {
        // Default value
        value: 'milligramsPerDecilitre',
        label_translation: 'milligrams_per_100_millilitres',
        maxValue: 500,
        minValue: 50,
        typeFormat: 'integer'
    },
    {
        value: 'millimolesPerLitre',
        label_translation: 'millimoles_per_litre',
        maxValue: 27.7,
        minValue: 2.8,
        typeFormat: 'decimal'
    }
];

export const PregnancyLogging = [
    {
        sortOrder: 1,
        value: 'physicalSymptomsTracking',
        label_translation: 'label_physical_symptoms'
    },
    {
        sortOrder: 2,
        value: 'sexualActivityTracking',
        label_translation: 'label_sexual_activity'
    },
    {
        sortOrder: 3,
        value: 'moodTracking',
        label_translation: 'label_mood'
    },
    {
        sortOrder: 4,
        value: 'sexDriveTracking',
        label_translation: 'label_sex_drive'
    },
    {
        sortOrder: 5,
        value: 'dischargeTracking',
        label_translation: 'label_discharge'
    }
];

export const validDueDate = (duedate) => {
    let valid = false;
    if (duedate) {
        let today = moment();
        let days = today.diff(duedate, 'days');
        valid = days <= 280;
    }
    return valid;
};

export const StopTrackingTypes = {
    Initial: {
        id: 0,
        flow: 0
    },
    Had: {
        id: 1,
        flow: 1
    },
    Loss: {
        id: 2,
        flow: 2
    },
    NotSay: {
        id: 3,
        flow: 3
    }
};

export const HydrationGoal = {
    oz: 80,
    minOz: 1,
    maxOz: MaxHydrationValues.goal.oz,
    ml: 2366,
    minMl: 1,
    maxMl: MaxHydrationValues.goal.ml,
    cups: 10,
    minCups: 1,
    maxCups: MaxHydrationValues.goal.cup
};

export const pushAnalyticsEvent = (action) => {
    AnalyticsUtil.push({
        event: 'track_event',
        category: "Women's Health Event",
        action: action
    });
};

export const Routes = {
    pregnancy: '/pregnancy-detail/',
    mct: '/menstrual-cycle-tracking/'
};

/**
 * Maps trimester value to currentPhase from /dayview endpoint
 */
export const TrimesterPhases = {
    First: 5,
    Second: 6,
    Third: 7
};

export const DaysInTrimester = {
    First: 91,
    Second: 98,
    Third: 91,
    Total: 280
};

export const sortBabyMovements = (list) => {
    return list.sort((a, b) => {
        return moment(b.startTimestamp).toDate() - moment(a.startTimestamp).toDate();
    });
};

export const sortGlucoseList = (list) => {
    return list.sort((a, b) => {
        return moment(b.userMeasurementTimestamp).toDate() - moment(a.userMeasurementTimestamp).toDate();
    });
};

export const getActivePregnancy = (pregnancySnapshots) => {
    let pregnancy = null;

    // Pregnancy is active if it doesn't have a delivery date or end date
    if (pregnancySnapshots) {
        pregnancy = pregnancySnapshots.find((item) => {
            return item.deliveryDate === null && item.endDate === null;
        });
    }

    return pregnancy;
};

/**
 *
 * @param {*} val is always expected to be in mg/dL
 * @param {*} fromUnit
 */
export const convertBloodGlucose = (val, fromUnit) => {
    if (fromUnit === 'millimolesPerLitre') {
        // Mmol to mgdl => Value * 18.0182 ( precision of 2 digit, rounding up)
        // Mgdl to mmol => Value * 0.0555 ( precision of 2 digit, rounding up)
        let converteVal = val * 0.0555;
        val = parseFloat(converteVal.toFixed(1));
    }
    return val;
};

export const MinuteStepCount = {
    Format12: 1,
    Format24: 1
};

export const messageSidebarReload = () => {
    if (window.postMessage) {
        window.postMessage('RELOAD_NAVIGATION', '*');
    }
};

export const isPregnancyDay = (pregnancySnapshots, date) => {
    return getPregnancyForDay(pregnancySnapshots, date) ? true : false;
};

export const findPregnancyForDateRange = (pregnancySnapshots, startDate, endDate) => {
    let start = moment(startDate, DateFormatter.LINK_FORMAT);
    const end = moment(endDate, DateFormatter.LINK_FORMAT);
    let pregnancy = null;
    while (start <= end) {
        pregnancy = getPregnancyForDay(pregnancySnapshots, start.toDate());
        if (pregnancy) {
            break;
        } else {
            start.add(1, 'days');
        }
    }
    return pregnancy;
};

export const isActivePregnancy = (pregnancy) => {
    return pregnancy && pregnancy.deliveryDate === null && pregnancy.endDate === null;
};

export const isValidDateRangeForPregnancy = (pregnancy, startDate, endDate) => {
    let start = moment(startDate);
    let end = moment(endDate);
    let valid = false;
    if (pregnancy) {
        valid =
            (start.isSameOrAfter(moment(pregnancy.pregnancyCycleStart)) ||
                end.isSameOrAfter(moment(pregnancy.pregnancyCycleStart))) &&
            end.isSameOrBefore(moment(pregnancy.deliveryDate || pregnancy.endDate || pregnancy.dueDate));
    }
    return valid;
};

export const getPregnancyForDay = (pregnancySnapshots, date) => {
    const compareDate = moment(date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    let pregnancy = null;

    if (pregnancySnapshots) {
        let copy = [...pregnancySnapshots];
        copy.sort((a, b) => {
            return moment(b.pregnancyCycleStart).toDate() - moment(a.pregnancyCycleStart).toDate();
        });

        pregnancy = copy.find((item) => {
            let found = false;
            // If the pregnancy start date is before the provided day, then we aren't interested in that pregnancy.
            if (compareDate.isSameOrAfter(moment(item.pregnancyCycleStart))) {
                if (item.deliveryDate === null && item.endDate === null) {
                    found = true; // if the pregnancy hasn't ended it will always be a pregnancy day.
                } else if (item.deliveryDate !== null && compareDate.isSameOrBefore(moment(item.deliveryDate))) {
                    found = true;
                } else if (item.endDate !== null && compareDate.isSameOrBefore(moment(item.endDate))) {
                    found = true;
                }
            }

            return found;
        });
    }
    return pregnancy;
};

export const getDaysInWeek = (viewDate, cycleStartDate, numberOfWeek) => {
    const compareDate = moment(viewDate).set({ hour: 0, minute: 0, second: 0 });
    let weekStart = moment(cycleStartDate).add(numberOfWeek - 1, 'weeks');
    let daysInWeek = compareDate.diff(weekStart, 'days') + 1;
    return daysInWeek;
};

export const getNumberOfWeekDayInWeek = (viewDate, cycleStartDate) => {
    const cycleStart = moment(cycleStartDate);
    const compareDate = moment(viewDate).set({ hour: 0, minute: 0, second: 0 });
    const numberOfWeek = compareDate.diff(cycleStart, 'weeks') + 1;
    const weekStart = cycleStart.add(numberOfWeek - 1, 'weeks');
    const daysInWeek = compareDate.diff(weekStart, 'days') + 1;
    return {
        numberOfWeek,
        daysInWeek
    };
};

export const getDeliveryEndDateMinMaxDates = (activePregnancy) => {
    const today = moment();
    const minDate = activePregnancy ? moment(activePregnancy.pregnancyCycleStart).toDate() : null;
    let maxDate = null;
    if (activePregnancy) {
        let tempDate = moment(activePregnancy.pregnancyCycleStart).add(45, 'weeks');
        if (today.isBefore(tempDate)) {
            maxDate = today.toDate();
        } else {
            maxDate = tempDate.toDate();
        }
    }

    return {
        maxDate: maxDate,
        minDate: minDate
    };
};

export const isPregnancyPhase = (currentPhase) => {
    if (typeof currentPhase !== 'undefined') {
        return (
            currentPhase === TrimesterPhases.First ||
            currentPhase === TrimesterPhases.Second ||
            currentPhase === TrimesterPhases.Third
        );
    }
    return false;
};

export const isPregnancySummary = (summary) => {
    return summary && isPregnancyPhase(summary.currentPhase);
};

export const navigateStopTrackingFlow = () => {
    NavigationUtil.navigate('/pregnancy-stop-tracking/1/0');
};

export const navigateSetupFlow = () => {
    // skip the get started page
    NavigationUtil.navigate('/pregnancy-tracking/true');
};

export const calculatePregnancyStartDate = (dueDate) => {
    return moment(dueDate)
        .subtract(279, 'days')
        .toDate();
};

export const getMinMaxDateValuesFromPregnancyStartDate = (pregnancyCycleDate) => {
    let beforeCycleStartDate = moment(pregnancyCycleDate)
        .subtract(14, 'days')
        .format(DateFormatter.LINK_FORMAT);
    let afterCycleStartDate = moment(pregnancyCycleDate)
        .add(14, 'days')
        .format(DateFormatter.LINK_FORMAT);
    return {
        startDate: beforeCycleStartDate,
        endDate: afterCycleStartDate
    };
};

export const getClosestWeightToPregnancyCycleStartDate = (pregnancyCycleDate, weightList) => {
    let closest = null;
    weightList.sort((a, b) => new Date(a.date) - new Date(b.date)); // Oldest to newest
    for (let i = 0; i < weightList.length; i++) {
        if (new Date(weightList[i].date) <= pregnancyCycleDate) {
            closest = weightList[i]; // Want the closest date before the cycle date
        } else if (closest === null) {
            closest = weightList[i]; // Or the closest one after the cycle date
            break;
        }
    }
    return closest;
};

export const PregnancyIcons = {
    Announcements: {
        key: 'Announcements',
        url: '/web-data/pregnancy/baby_announcement.json'
    },
    Delivery: {
        key: 'Delivery',
        url: '/web-data/pregnancy/baby_delivery.json'
    },
    GENERAL: {
        key: 'GENERAL',
        url: '/web-data/pregnancy/babymovement_general.json'
    },
    KICKS: {
        key: 'KICKS',
        url: '/web-data/pregnancy/babymovement_kick.json'
    },
    HICCUPS: {
        key: 'HICCUPS',
        url: '/web-data/pregnancy/babymovement_hiccups.json'
    }
};

export const isSameGlucoseTime = (time, time2) => {
    return (
        DateFormatter.formatLocalizeTime(moment(time).toDate()) ===
        DateFormatter.formatLocalizeTime(moment(time2).toDate())
    );
};

export const hasSetupFailed = (userSettings, pregnancySnapshots) => {
    let failed = false;
    if (
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        userSettings.userMenstrualCycleSettings.menstrualCycleType === CYCLE_TYPE_PREGNANT
    ) {
        if (pregnancySnapshots !== null) {
            if (pregnancySnapshots.length === 0) {
                failed = true;
            } else {
                let pregnancy = pregnancySnapshots.find((item) => {
                    return item.deliveryDate === null && item.endDate === null;
                });

                failed = pregnancy === undefined;
            }
        }
    }
    return failed;
};

const getSnapshots = async () => {
    const url = '/periodichealth-service/menstrualcycle/pregnancysnapshot/all';
    return await gcApi.get(url);
};

export const hasCycleSnapshotMismatch = async (userSettings, data, callBack) => {
    let mismatch = false;
    if (
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        userSettings.userMenstrualCycleSettings.menstrualCycleType !== CYCLE_TYPE_PREGNANT
    ) {
        let snapshots = [];
        if (data) {
            snapshots = data;
        } else {
            // Need to get the latest pregnancy snapshots to compare
            let response = await getSnapshots();
            snapshots = response && response.data ? response.data : [];
        }
        let activePregnancy = snapshots.find((item) => {
            return item.deliveryDate === null && item.endDate === null;
        });
        mismatch = activePregnancy !== undefined;
    }
    callBack(mismatch);
};

export const setMenstrualCyclePregnant = (success = () => {}, error = () => {}) => {
    gcApi
        .put('/userprofile-service/userprofile/user-settings', {
            userMenstrualCycleSettings: {
                menstrualCycleType: CYCLE_TYPE_PREGNANT
            }
        })
        .then(() => {
            success();
        })
        .catch((err) => {
            error();
        });
};

export const ReportTypes = {
    Symptoms: 'symptoms',
    BloodGlucose: 'blood_glucose',
    BabyMovements: 'baby_movement',
    Notes: 'notes'
};

// Copied from reducers/menstrualCycleTracking/menstrualCycleTracking.js
const DefaultSymptomTypes = {
    symptoms: [
        'ACNE',
        'BACKACHE',
        'BLOATING',
        'BODY_ACHES',
        'CONSTIPATION',
        'CRAMPS',
        'CRAVINGS',
        'DIARRHEA',
        'FATIGUE',
        'HEADACHE',
        'INSOMNIA',
        'NAUSEA',
        'TENDER_BREASTS',
        'CHILLS',
        'DISTRACTED',
        'DRY_SKIN',
        'FREQUENT_URINATION',
        'HAIR_LOSS',
        'HOT_FLASHES',
        'NIGHT_SWEATS',
        'URINE_LEAKAGE',
        'VAGINAL_DRYNESS',
        'WEIGHT_GAIN'
    ],
    moods: [
        'FINE',
        'HAPPY',
        'ENERGETIC',
        'MOTIVATED',
        'MOOD_SWINGS',
        'SAD',
        'EMOTIONAL',
        'OVERWHELMED',
        'IRRITABLE',
        'FRUSTRATED'
    ],
    discharge: ['NO_DISCHARGE', 'CREAMY', 'EGG_WHITE', 'SPOTTING', 'STICKY', 'UNUSUAL'],
    sexualActivity: ['PROTECTED', 'UNPROTECTED'],
    sexDrive: ['LOW', 'AVERAGE', 'HIGH'],
    flow: ['LIGHT', 'MEDIUM', 'HEAVY']
};

export const SymptomTypeFilter = {
    REGULAR: DefaultSymptomTypes,
    IRREGULAR: DefaultSymptomTypes,
    NONE: DefaultSymptomTypes,
    PREGNANT: {
        symptoms: [
            'ACNE',
            'BACKACHE',
            'BLOATING',
            'BODY_ACHES',
            'CONSTIPATION',
            'CONTRACTIONS',
            'CRAMPS',
            'CRAVINGS',
            'DIARRHEA',
            'DIZZINESS',
            'FATIGUE',
            'HEADACHE',
            'INSOMNIA',
            'NAUSEA',
            'TENDER_BREASTS',
            'TROUBLE_BREATHING',
            'VOMITING',
            'FREQUENT_URINATION',
            'SWELLING',
            'HEARTBURN',
            'ROUND_LIGAMENT_PAIN'
        ],
        moods: [
            'FINE',
            'HAPPY',
            'ENERGETIC',
            'MOTIVATED',
            'MOOD_SWINGS',
            'SAD',
            'EMOTIONAL',
            'OVERWHELMED',
            'IRRITABLE',
            'FRUSTRATED',
            'WORRIED'
        ],
        discharge: ['NO_DISCHARGE', 'LIGHT', 'MEDIUM', 'HEAVY', 'SPOTTING'],
        sexualActivity: ['PROTECTED', 'UNPROTECTED'],
        sexDrive: ['LOW', 'AVERAGE', 'HIGH']
    }
};

export const filterSupportedSymptoms = (symptomType, symptoms, menstrualCycleType) => {
    return symptoms.filter((symptom) => {
        return (
            SymptomTypeFilter[menstrualCycleType] &&
            SymptomTypeFilter[menstrualCycleType][symptomType] &&
            SymptomTypeFilter[menstrualCycleType][symptomType].indexOf(symptom) > -1
        );
    });
};

// The max value of mmol/L when converted to mg/dl can be higher than the max allowed
export const bloodGlucoseMaxCheck = (value, glucoseMeasurementUnit) => {
    const gluCoseObj = glucoseUnits.find((item) => {
        return item.value === glucoseMeasurementUnit;
    });
    if (value > gluCoseObj.maxValue) {
        value = gluCoseObj.maxValue;
    }
    return value;
};

export const create4WeekBuckets = (pregnancyCycleStart, startDate, endDate) => {
    let fourWeekBuckets = [];
    let nextWeek = moment(startDate, DateFormatter.LINK_FORMAT);
    const end = moment(endDate, DateFormatter.LINK_FORMAT);

    fourWeekBuckets.push({
        startWeek: getNumberOfWeekDayInWeek(nextWeek.format(DateFormatter.LINK_FORMAT), pregnancyCycleStart)
            .numberOfWeek,
        start: nextWeek.format(DateFormatter.LINK_FORMAT),
        end: nextWeek.add(27, 'day').format(DateFormatter.LINK_FORMAT),
        endWeek: getNumberOfWeekDayInWeek(nextWeek.format(DateFormatter.LINK_FORMAT), pregnancyCycleStart).numberOfWeek,
        items: []
    });

    while (nextWeek.isBefore(end)) {
        let start = nextWeek.add(1, 'day').format(DateFormatter.LINK_FORMAT);
        let endDateVal = nextWeek.add(27, 'day').format(DateFormatter.LINK_FORMAT);
        fourWeekBuckets.push({
            startWeek: getNumberOfWeekDayInWeek(start, pregnancyCycleStart).numberOfWeek,
            start: start,
            end: endDateVal,
            endWeek: getNumberOfWeekDayInWeek(endDateVal, pregnancyCycleStart).numberOfWeek,
            items: []
        });
    }
    return fourWeekBuckets;
};

export const isCurrentMCTDay = (reportDate, userSettings) => {
    const today = moment().format(DateFormatter.LINK_FORMAT);

    return (
        reportDate === today &&
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        userSettings.userMenstrualCycleSettings.menstrualCycleType !== CYCLE_TYPE_PREGNANT
    );
};

export const isPregnancySetup = (userSettings) => {
    return !!(
        userSettings &&
        userSettings.userMenstrualCycleSettings &&
        userSettings.userMenstrualCycleSettings.pregnancySetup
    );
};

export const getInitialCycleStartDate = async (strDate = moment().format(DateFormatter.LINK_FORMAT), mctCycleType) => {
    let returnObj = {
        cycleType: null,
        startDate: null,
        periodStartDate: null
    };
    const MCT_CYCLE_TYPES = [CYCLE_TYPE_REGULAR, CYCLE_TYPE_IRREGULAR];
    const dayAdd = (cycleType) => {
        return MCT_CYCLE_TYPES.includes(cycleType) && MCT_CYCLE_TYPES.includes(mctCycleType) ? 0 : 1;
    };
    let today = moment();
    let { data } = await gcApi.get(`/periodichealth-service/menstrualcycle/lastconfirmed/${strDate}`);
    if (data) {
        let periodStartDate = moment(data.periodStartDate);
        if (data.cycleType === CYCLE_TYPE_PREGNANT) {
            periodStartDate.add(data.cycleLength, 'day');
            if (periodStartDate.isSameOrBefore(today, 'day')) {
                periodStartDate = periodStartDate.toDate();
            } else {
                periodStartDate = null;
            }
        } else {
            periodStartDate = periodStartDate.isSame(today, 'day')
                ? null
                : periodStartDate.add(dayAdd(data.cycleType), 'day').toDate();
        }
        returnObj.cycleType = data.cycleType;
        returnObj.startDate = periodStartDate;
        returnObj.periodStartDate = data.periodStartDate;
    }

    return returnObj;
};

export const getPregnancyConflictModal = (open, onClose, onClick) => {
    return getDialog('conflicting_dates', 'pregnancy_conflict_date', open, onClose, onClick);
};

export const getMenstrualCycleTrackingConflictModal = (open, onClose, onClick) => {
    return getDialog('conflicting_dates', 'mct_conflict_date', open, onClose, onClick);
};

const getDialog = (title_key, content_key, open = false, onClose = () => {}, onClick = () => {}) => {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>
                <Translate ns="pregnancy" content={title_key} />
            </DialogTitle>
            <DialogContent>
                <Translate ns="pregnancy" content={content_key} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClick}>
                    <Translate ns="common" content="button_ok" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const setMenstrualCycleInactive = (success = () => {}, error = () => {}) => {
    const timeStampGmt = DateFormatter.formatConnectTimestampUTC(moment().toDate());
    gcApi
        .put('/userprofile-service/userprofile/user-settings', {
            userMenstrualCycleSettings: {
                menstrualCycleType: CYCLE_TYPE_INACTIVE,
                inactiveReportTimestamp: timeStampGmt
            }
        })
        .then(() => {
            success();
        })
        .catch((err) => {
            error();
        });
};

export const BLOOD_GLUCOSE_TYPES = ['BEFOREMEAL', 'AFTERMEAL', 'BEFOREBED', 'OTHER'];
export const BABY_MOVEMENT_TYPES = ['GENERAL', 'KICKS', 'HICCUPS'];

export const getPregnancyDefaultTitle = (i18next, date) => {
    return (
        i18next.t('menstrual-cycle-tracking:label_pregnancy') +
        ' ' +
        moment(date, DateFormatter.LINK_FORMAT).format('YYYY')
    );
};

export const getChartTitle = (numberOfWeek, daysInWeek, className, noSpace) => {
    let currentNumberOfWeek = numberOfWeek - 1;
    let title = '';

    if (currentNumberOfWeek === 0) {
        title = i18next.t('pregnancy:lbl_short_day_no_space', { 0: daysInWeek });
    } else if (currentNumberOfWeek < 42) {
        const weekAndDayId = noSpace ? 'pregnancy:lbl_week_and_day_no_space' : 'pregnancy:lbl_week_and_day';
        title = i18next.t(weekAndDayId, { 0: currentNumberOfWeek, 1: daysInWeek });
    } else {
        title = i18next.t('pregnancy:greater_than_42');
    }

    return <span className={className}>{title}</span>;
};
